/**
 * GA4 + GTM analytics helper for CakeRestro.
 * All gtag/dataLayer calls route through here so they are safe in SSR/prerender
 * (react-snap) where `window.gtag` does not exist.
 */

const GA_ID = 'G-7QTPSL5CFZ';

function getDataLayer() {
  if (typeof window === 'undefined') return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/**
 * Push to dataLayer via gtag-compatible signature.
 * Works whether the global `gtag` function exists or only the dataLayer is present.
 */
export function track(eventName, eventParams) {
  const dl = getDataLayer();
  if (!dl) return;
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventParams || {});
  } else {
    dl.push({ event: eventName, ...(eventParams || {}) });
  }
}

/**
 * Map a CakeRestro product object into the GA4 `items[]` schema.
 * Accepts either the mapped site product (from ProductService) or a raw cart item.
 */
export function toGtagItems(productOrItems, opts = {}) {
  const list = Array.isArray(productOrItems) ? productOrItems : [productOrItems];
  return list
    .filter(Boolean)
    .map((p, index) => ({
      item_id: String(p.id || p._productId || ''),
      item_name: p.name || '',
      item_category: p.category || p.parentCategory || '',
      item_variant: p.weight || p.variantLabel || (Array.isArray(p.variants) && p.variants[0] ? p.variants[0].label : ''),
      price: Number(p.price) || 0,
      quantity: Number(p.quantity) || 1,
      index: index + (opts.index || 0),
      ...(p.discount ? { discount: Number(p.discount) } : {}),
      ...(opts.list_id ? { item_list_id: opts.list_id } : {}),
      ...(opts.list_name ? { item_list_name: opts.list_name } : {}),
    }));
}

export const analytics = {
  GA_ID,
  track,
  toGtagItems,

  // --- Ecommerce events -------------------------------------------------
  viewItemList(products, opts = {}) {
    track('view_item_list', {
      currency: 'INR',
      item_list_id: opts.list_id || 'products',
      item_list_name: opts.list_name || 'Products',
      items: toGtagItems(products, opts),
    });
  },

  selectItem(product, opts = {}) {
    track('select_item', {
      currency: 'INR',
      item_list_id: opts.list_id || 'products',
      item_list_name: opts.list_name || 'Products',
      items: toGtagItems(product, opts),
    });
  },

  viewItem(product) {
    track('view_item', {
      currency: 'INR',
      value: Number(product.price) || 0,
      items: toGtagItems(product),
    });
  },

  addToCart(product) {
    track('add_to_cart', {
      currency: 'INR',
      value: (Number(product.price) || 0) * (Number(product.quantity) || 1),
      items: toGtagItems(product),
    });
  },

  removeFromCart(product) {
    track('remove_from_cart', {
      currency: 'INR',
      value: (Number(product.price) || 0) * (Number(product.quantity) || 1),
      items: toGtagItems(product),
    });
  },

  viewCart(items) {
    const value = (items || []).reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 1), 0);
    track('view_cart', { currency: 'INR', value, items: toGtagItems(items) });
  },

  addToWishlist(product) {
    track('add_to_wishlist', { currency: 'INR', items: toGtagItems(product) });
  },

  removeFromWishlist(product) {
    track('remove_from_wishlist', { currency: 'INR', items: toGtagItems(product) });
  },

  beginCheckout(items, opts = {}) {
    const value = (items || []).reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 1), 0);
    track('begin_checkout', {
      currency: 'INR',
      value,
      coupon: opts.coupon || undefined,
      items: toGtagItems(items),
    });
  },

  addShippingInfo(items, opts = {}) {
    track('add_shipping_info', {
      currency: 'INR',
      shipping_tier: opts.shipping_tier || '',
      items: toGtagItems(items),
    });
  },

  addPaymentInfo(items, opts = {}) {
    const value = (items || []).reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 1), 0);
    track('add_payment_info', {
      currency: 'INR',
      value,
      payment_type: opts.payment_type || 'razorpay',
      coupon: opts.coupon || undefined,
      items: toGtagItems(items),
    });
  },

  purchase(order, items) {
    track('purchase', {
      currency: 'INR',
      transaction_id: order.transaction_id || order.orderId || '',
      value: Number(order.value) || 0,
      tax: Number(order.tax) || 0,
      shipping: Number(order.shipping) || 0,
      coupon: order.coupon || undefined,
      items: toGtagItems(items),
    });
  },

  // --- Promotions --------------------------------------------------------
  viewPromotion(banner, opts = {}) {
    track('view_promotion', {
      creative_name: opts.creative_name || banner?.title || '',
      creative_slot: opts.creative_slot || 'home_banner',
      promotion_id: opts.promotion_id || String(banner?.id || ''),
      promotion_name: opts.promotion_name || banner?.title || banner?.subtitle || '',
    });
  },

  selectPromotion(banner, opts = {}) {
    track('select_promotion', {
      creative_name: opts.creative_name || banner?.title || '',
      creative_slot: opts.creative_slot || 'home_banner',
      promotion_id: opts.promotion_id || String(banner?.id || ''),
      promotion_name: opts.promotion_name || banner?.title || banner?.subtitle || '',
    });
  },

  // --- Search ------------------------------------------------------------
  viewSearchResults(searchTerm) {
    track('view_search_results', { search_term: searchTerm || '' });
  },
};

export default analytics;